import type { ErrorType, ValidationError } from './patient-medication-history.rest'
import type { IPatientId } from '../patient/patient.interface'


export type PatientMedicationId = string
export type PatientMedicationRecordId = string
export type PatientMedicationRecordEntryId = string
export type PatientMedicationDosage = string
export type PatientMedicationDosageUnitId = string
export type PatientMedicationFrequency = number
export type PatientMedicationFrequencyUnitId = string
export type PatientMedicationRouteId = string
export type PatientMedicationIndications = string
export type PatientMedicationPrescriber = string
export type PatientMedicationComments = string
export type PatientMedicationRecommendations = string

export interface MedicationDosageUnit {
  id: PatientMedicationDosageUnitId
  name: string
}

export interface MedicationFrequencyUnit {
  id: PatientMedicationFrequencyUnitId
  name: string
}

export interface MedicationRoute {
  id: PatientMedicationRouteId
  name: string
}

export interface PatientMedication {
  id: PatientMedicationId
  patientId: IPatientId
  medicationDosageUnitId: string
  medicationRouteId: string
  name: string
}

export interface PatientMedicationRecord {
  id: PatientMedicationRecordId
  patientId: IPatientId
  activeDate: string
}

export enum PatientMedicationRecordEntryStatus {
  Active = 'active',
  Discontinued = 'discontinued',
}

export interface PatientMedicationRecordEntry {
  patientMedicationId: PatientMedicationId
  patientMedicationRecordId: PatientMedicationRecordId
  id?: PatientMedicationRecordEntryId
  status: PatientMedicationRecordEntryStatus
  dosage?: PatientMedicationDosage
  frequency?: PatientMedicationFrequency
  frequencyUnitId?: PatientMedicationFrequencyUnitId
  indications?: PatientMedicationIndications
  prescriber?: PatientMedicationPrescriber
  comments?: PatientMedicationComments
  recommendations?: PatientMedicationRecommendations
}

export interface PatientMedicationRecordEntryDefault {
  dosage?: PatientMedicationDosage
  frequency?: PatientMedicationFrequency
  frequencyUnitId?: PatientMedicationFrequencyUnitId
  indications?: PatientMedicationIndications
  prescriber?: PatientMedicationPrescriber
}

export interface PatientMedicationHistory {
  patientId: IPatientId
  medications: PatientMedication[]
  medicationRecords: PatientMedicationRecord[]
  medicationRecordEntries: PatientMedicationRecordEntry[]
}

export interface IUsePatientMedicationHistory {
  addMedication: (
    medicationDosageUnitId: PatientMedicationDosageUnitId,
    medicationRouteId: PatientMedicationRouteId,
    name: string,
  ) => void
  addMedicationRecord: (activeDate: string) => void
  addOrUpdateMedicationRecordEntry: (
    patientMedicationId: PatientMedicationId,
    patientMedicationRecordId: PatientMedicationRecordId,
    status: PatientMedicationRecordEntryStatus,
    dosage: PatientMedicationDosage,
    frequency: PatientMedicationFrequency,
    frequencyUnitId: PatientMedicationFrequencyUnitId,
    indications?: PatientMedicationIndications,
    prescriber?: PatientMedicationPrescriber,
    comments?: PatientMedicationComments,
    recommendations?: PatientMedicationRecommendations,
  ) => void
  deleteMedication: (patientMedicationId: PatientMedicationId) => void
  deleteMedicationRecord: (
    patientMedicationRecordId: PatientMedicationRecordId,
  ) => void
  errorType: ErrorType
  isError: boolean
  isLoading: boolean
  patientMedicationHistory: PatientMedicationHistory
  updateMedication: (
    patientMedicationId: PatientMedicationId,
    medicationDosageUnitId: PatientMedicationDosageUnitId,
    medicationRouteId: PatientMedicationRouteId,
    name: string,
  ) => void
  updateMedicationRecord: (
    patientMedicationRecordId: PatientMedicationRecordId,
    activeDate: string,
  ) => void
  validationErrors: ValidationError[]
}

export interface IUseMedicationDosageUnits {
  medicationDosageUnits: MedicationDosageUnit[]
  isLoading: boolean
  isError: boolean
}

export interface IUseMedicationFrequencyUnits {
  medicationFrequencyUnits: MedicationFrequencyUnit[]
  isLoading: boolean
  isError: boolean
}

export interface IUseMedicationRoutes {
  medicationRoutes: MedicationRoute[]
  isLoading: boolean
  isError: boolean
}

export interface IUseRecordEntryDefaultValues {
  recordEntryDefaults: PatientMedicationRecordEntryDefault
  getRecordEntryDefaults: (
    patientId: string,
    patientMedicationId: PatientMedicationId,
    patientMedicationRecordId: PatientMedicationRecordId,
  ) => void
}
